import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import api, { fmtApiError } from "./api";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(fmtApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen flex items-center justify-center bg-ivory p-6">
      <div className="w-full max-w-md rounded-3xl bg-white border border-pine/10 shadow-lift p-8 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <img src="/dyntra-logo.jpg" alt="Dyntra" className="h-10 w-10 rounded-full object-cover ring-2 ring-gold/60" />
          <p className="font-display text-lg font-semibold text-pine-deep">Dyntra <span className="text-teal-brand italic">Connect</span></p>
        </div>
        {!token ? (
          <p className="text-sm text-slate-600">This reset link is missing its token. Please request a new one from the sign-in page.</p>
        ) : done ? (
          <div data-testid="reset-success" className="text-center py-4">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-teal-soft"><CheckCircle2 className="h-7 w-7 text-teal-brand" /></span>
            <h2 className="mt-4 font-display text-2xl font-semibold text-pine-deep">Password updated</h2>
            <p className="mt-2 text-sm text-slate-600">Sign in with your new password.</p>
            <button data-testid="reset-goto-login" onClick={() => nav("/connect")} className="mt-6 w-full rounded-full bg-pine px-6 py-3.5 text-sm font-bold text-ivory hover:bg-pine-deep transition-colors">
              Go to Sign In
            </button>
          </div>
        ) : (
          <>
            <h2 className="font-display text-2xl font-semibold text-pine-deep">Set a new password</h2>
            <p className="mt-1.5 text-sm text-slate-500">Choose a new password for your Dyntra Connect account.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-pine/60">New Password</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-pine/40" />
                  <input data-testid="reset-password-input" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Minimum 8 characters" className="h-12 w-full rounded-xl border border-pine/15 bg-white pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-pine/60">Confirm Password</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-pine/40" />
                  <input data-testid="reset-password-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat the new password" className="h-12 w-full rounded-xl border border-pine/15 bg-white pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine" />
                </div>
              </div>
              {error && <p data-testid="reset-error" className="text-sm font-semibold text-red-600">{error}</p>}
              <button type="submit" data-testid="reset-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-bold text-ivory shadow-lift transition-all hover:bg-pine-deep disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy ? "Updating..." : "Update Password"}
              </button>
            </form>
          </>
        )}
        <Link to="/connect" className="mt-6 block text-center text-xs font-bold text-teal-brand hover:text-pine transition-colors">← Back to Sign In</Link>
      </div>
    </div>
  );
}
